/**
 * DeletePropertyModal - Confirmation dialog for deleting property types
 *
 * Requires typing "DELETE" to confirm. Shows usage count and accessible warnings.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw01 } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { ConfirmationDialog } from '@/lib/base/overlays';
import {
  getUserFriendlyEventError,
  PERMISSION_DENIED_MESSAGE,
} from '@/lib/crm/core/errors';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import { createEventsClient } from '@/lib/crm/core/EventsClient';
import {
  createSchemaClient,
  type PropertyType,
} from '@/lib/crm/core/SchemaClient';
import { useViewHealthScope } from '@/lib/view-health';

/**
 * Entity type display names.
 */
const ENTITY_TYPE_DISPLAY: Record<string, string> = {
  ahoy_contact: 'contact',
  ahoy_company: 'company',
  ahoy_deal: 'deal',
  contacts: 'contact',
  companies: 'company',
  deals: 'deal',
};

export interface DeletePropertyModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when the modal is closed */
  onClose: () => void;
  /** Property to delete */
  property: PropertyType | null;
  /** The entity type name (contacts, companies, deals) */
  entityTypeName: string;
  /** Callback when the delete succeeds */
  onConfirm?: () => void;
}

/**
 * Delete Property Modal component.
 */
export function DeletePropertyModal({
  isOpen,
  onClose,
  property,
  entityTypeName,
  onConfirm,
}: DeletePropertyModalProps) {
  useViewHealthScope('overlay', 'delete-property-modal', isOpen && !!property);
  const { currentOrg } = useOrg();
  const { user } = useAuthInfo();
  const queryClient = useQueryClient();
  const orgId = currentOrg?.id;
  const userId = user?.id;

  // Map tab ID to CRM type name - handle legacy tab IDs (contacts/companies/deals)
  const crmTypeName =
    {
      contacts: 'ahoy_contact',
      companies: 'ahoy_company',
      deals: 'ahoy_deal',
    }[entityTypeName] || entityTypeName;
  const crmTypeDisplay =
    ENTITY_TYPE_DISPLAY[crmTypeName] ||
    ENTITY_TYPE_DISPLAY[entityTypeName] ||
    'object';

  // Form state
  const [confirmText, setConfirmText] = useState('');
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch usage count
  const {
    data: usageCount,
    isLoading: isLoadingUsage,
    error: usageError,
    refetch: refetchUsage,
  } = useQuery({
    queryKey: [
      'propertyTypeUsageCount',
      orgId,
      crmTypeName,
      property?.property_type_name,
    ],
    queryFn: async () => {
      if (!orgId || !property) {
        throw new Error('Missing required data');
      }
      const client = createSchemaClient(orgId);
      return client.getPropertyTypeUsageCount(
        crmTypeName,
        property.property_type_name
      );
    },
    enabled: !!orgId && !!property && isOpen,
    retry: false,
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmText('');
      setApiError(null);
    }
  }, [isOpen]);

  // Check if confirm is enabled
  const isConfirmEnabled = confirmText === 'DELETE' && !isSubmitting;

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!isConfirmEnabled || !orgId || !userId || !property) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const eventsClient = createEventsClient(orgId);
      const builder = createEventBuilder({
        sourceType: 'ahoy_user',
        sourceId: String(userId),
        startSeq: property.last_event_seq,
      });

      builder.objectDeleted(property.entity_id, 'ahoy_property_type');

      await eventsClient.submitEvents(builder.build());

      // Invalidate query cache
      await queryClient.invalidateQueries({
        queryKey: ['propertyTypes', orgId, crmTypeName],
      });

      // Call success callback
      onConfirm?.();

      // Close modal
      onClose();
    } catch (error) {
      setApiError(getUserFriendlyEventError(error));
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isConfirmEnabled,
    orgId,
    userId,
    property,
    crmTypeName,
    queryClient,
    onConfirm,
    onClose,
  ]);

  // Retry after error — catch the rethrow from handleSubmit since
  // this call path bypasses ConfirmationDialog's built-in try/catch
  const handleRetry = useCallback(async () => {
    setApiError(null);
    try {
      await handleSubmit();
    } catch {
      // error state already set in handleSubmit
    }
  }, [handleSubmit]);

  // Usage alert content
  const usageAlert = useMemo(() => {
    let alertClassName: string;
    if (isLoadingUsage) {
      alertClassName =
        'bg-[var(--color-bg-secondary)] border-[var(--color-border-primary)]';
    } else if (usageCount === 0 && !usageError) {
      alertClassName =
        'bg-[var(--color-bg-secondary)] border-[var(--color-border-primary)]';
    } else {
      alertClassName = 'bg-warning-50 border-warning-200';
    }

    let usageContent: React.ReactNode;
    if (isLoadingUsage) {
      usageContent = (
        <p className='text-sm text-[var(--color-text-secondary)]'>
          Loading usage count…
        </p>
      );
    } else if (usageError) {
      usageContent = (
        <div className='flex items-center justify-between'>
          <p className='text-sm text-warning-700'>Could not load usage count</p>
          <Button
            size='sm'
            color='secondary'
            iconLeading={RefreshCw01}
            onClick={() => refetchUsage()}
          >
            Retry
          </Button>
        </div>
      );
    } else if (usageCount === 0) {
      usageContent = (
        <p className='text-sm text-[var(--color-text-secondary)]'>
          This property is not used on any {crmTypeDisplay}s.
        </p>
      );
    } else {
      usageContent = (
        <>
          <p className='text-sm font-medium text-warning-700 mb-1'>
            This property is used on {usageCount} {crmTypeDisplay}
            {usageCount !== 1 ? 's' : ''}.
          </p>
          <p className='text-sm text-warning-600'>
            Deleting will hide this property but preserve existing data.
          </p>
        </>
      );
    }

    return (
      <div
        role='alert'
        className={`mb-4 p-4 rounded-lg border ${alertClassName}`}
      >
        {usageContent}
      </div>
    );
  }, [isLoadingUsage, usageError, usageCount, crmTypeDisplay, refetchUsage]);

  if (!isOpen || !property) {
    return null;
  }

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      onCancel={onClose}
      onConfirm={handleSubmit}
      title='Delete property'
      description={`Are you sure you want to delete "${property.label}"?`}
      confirmLabel='Delete Property'
      variant='destructive'
      isLoading={isSubmitting}
      isConfirmDisabled={confirmText !== 'DELETE' || isSubmitting}
    >
      {/* API Error Banner */}
      {apiError && (
        <div className='mb-4 rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
          <div className='flex items-start gap-3'>
            <FeaturedIcon
              icon={AlertTriangle}
              size='sm'
              theme='light-outline'
              color='error'
            />
            <div className='flex-1'>
              <h4 className='text-sm font-medium text-utility-error-700'>
                {apiError === PERMISSION_DENIED_MESSAGE
                  ? 'Permission denied'
                  : 'Error'}
              </h4>
              <p className='text-sm text-utility-error-600 mt-1'>{apiError}</p>
            </div>
            {apiError !== PERMISSION_DENIED_MESSAGE && (
              <Button
                size='sm'
                color='secondary'
                iconLeading={RefreshCw01}
                onClick={handleRetry}
              >
                Retry
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Usage alert */}
      {usageAlert}

      {/* Type to confirm */}
      <div>
        <Input
          label={
            <span>
              Type <span className='font-mono font-bold'>DELETE</span> to
              confirm
            </span>
          }
          placeholder='DELETE'
          value={confirmText}
          onChange={setConfirmText}
          isDisabled={isSubmitting}
          aria-describedby='delete-confirm-hint'
        />
        <p id='delete-confirm-hint' className='sr-only'>
          Type the word DELETE in capital letters to enable the delete button
        </p>
      </div>
    </ConfirmationDialog>
  );
}
